import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import {
  FormGroup,
  Label,
  Input,
  Button,
  Col,
  Card,
  CardBody,
  InputGroup,
  InputGroupAddon,
} from 'reactstrap';
import Select from '../../Template/Input/Select';
import oikosApi from '../../../services/oikos';
import qs from 'qs';
import { notifyError, notifySuccess } from '../../../utils/notify';
import { TextareaAutosize } from '@mui/material';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import { sendFormEmail } from '../../../utils/fc';
import { linkProducaoVM } from '../../../utils/linksOikos/link';
import {
  EMAIL_TEXT_CONVITE_PROPOSTA,
  buildPedidoCompraEmailContextHtml,
} from './pedidoCompraEmailTexts';
import { EQUIPE_CATEG_GR_ID } from '../ModalPedidoCompra/ModalPedidoCompra';
import '../ModalPedidoCotacao/ModalPedidoCotacao.css';

// Um pedidoCompra é uma "vaga" quando o orcItem vinculado a ele possui
// categGr_id = Equipe, não mais a partir de um pedidoCompraTp_id específico.
const isPedidoCompraVaga = (dataPedido) => Number(dataPedido?.categGr_id) === EQUIPE_CATEG_GR_ID;

const linkASerUsadoVM = linkProducaoVM;

const b64urlEncode = (str) => {
  const bytes = new TextEncoder().encode(str);
  let bin = '';
  bytes.forEach((b) => (bin += String.fromCharCode(b)));
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const DisparoEmailsCotacaoForm = forwardRef(({
  pedidoCompraData = {},
  fetchPedidoCompraDocuments,
  disabled = false,
  hideBancoFornecedores = false,
  hideLinkCompartilhamentoAberto = false,
}, ref) => {
  const [arquivosLinks, setArquivosLinks] = useState([]);
  const [corpoEmailEspecifico, setCorpoEmailEspecifico] = useState('');
  const [corpoEmailFornecedor, setCorpoEmailFornecedor] = useState('');
  const [inputsEmailsEspecificos, setInputsEmailsEspecificos] = useState([]);
  const [inputEmailEspecifico, setInputEmailEspecifico] = useState({});
  const [inputsEmailsFornecedores, setInputsEmailsFornecedores] = useState([]);
  const [inputEmailFornecedor, setInputEmailFornecedor] = useState({});
  const [selFornecedores, setSelFornecedores] = useState([]);
  const [isSending, setIsSending] = useState(false);

  const resetForm = () => {
    setArquivosLinks([]);
    setCorpoEmailEspecifico('');
    setCorpoEmailFornecedor('');
    setInputsEmailsEspecificos([]);
    setInputEmailEspecifico({});
    setInputsEmailsFornecedores([]);
    setInputEmailFornecedor({});
    setSelFornecedores([]);
    setIsSending(false);
  };

  useEffect(() => {
    let cancelled = false;

    const loadDocs = async () => {
      const pedidoCompraId = Number(pedidoCompraData?.pedidoCompra_id) || 0;
      if (!pedidoCompraId || !fetchPedidoCompraDocuments) {
        setArquivosLinks([]);
        return;
      }

      try {
        const docs = await fetchPedidoCompraDocuments(pedidoCompraId, 'pedidoCompra');
        if (cancelled) return;
        const links = (docs || [])
          .filter((doc) => doc?.data?.path)
          .map((doc) => ({
            link: `${linkASerUsadoVM}/${doc.data.path}`,
            desc: doc.props?.description || 'Documento relacionado à cotação',
          }));
        setArquivosLinks(links);
      } catch (e) {
        if (!cancelled) setArquivosLinks([]);
      }
    };

    loadDocs();
    return () => { cancelled = true; };
  }, [pedidoCompraData?.pedidoCompra_id, fetchPedidoCompraDocuments]);

  const checkCamposObrigatoriosEmailEspecifico = () => {
    if (!inputEmailEspecifico.inputEmailEspecifico_email) {
      notifyError('O campo "E-mail" é obrigatório.');
      return false;
    }
    return true;
  };

  const montaURLFormLinkAberto = (data) => {
    const vmBase = `${linkASerUsadoVM}formAberto.php#`;
    const hash = b64urlEncode(JSON.stringify(data));
    return `${vmBase}${hash}`;
  };

  const montaURLFormLinkEmailEspecifico = (data) => {
    const vmBase = `${linkASerUsadoVM}formEspecifico.php#`;
    const hash = b64urlEncode(JSON.stringify(data));
    return `${vmBase}${hash}`;
  };

  const montaURLFormLinkEmailFornecedor = (data) => {
    const vmBase = `${linkASerUsadoVM}formFornecedor.php#`;
    const hash = b64urlEncode(JSON.stringify(data));
    return `${vmBase}${hash}`;
  };

  const handleAddEmailEspecifico = () => {
    if (disabled) return;
    if (!checkCamposObrigatoriosEmailEspecifico()) return;
    setInputsEmailsEspecificos([...inputsEmailsEspecificos, { ...inputEmailEspecifico }]);
    setInputEmailEspecifico({});
  };

  const handleRemoveEmailEspecifico = (index) => {
    if (disabled) return;
    setInputsEmailsEspecificos(inputsEmailsEspecificos.filter((_, i) => i !== index));
  };

  const handleAddEmailFornecedor = () => {
    if (disabled) return;
    if (!inputEmailFornecedor?.fornecedor_id) {
      notifyError('Selecione um fornecedor.');
      return;
    }
    setInputsEmailsFornecedores([...inputsEmailsFornecedores, inputEmailFornecedor]);
    setInputEmailFornecedor({});
  };

  const handleRemoveEmailFornecedor = (index) => {
    if (disabled) return;
    setInputsEmailsFornecedores(inputsEmailsFornecedores.filter((_, i) => i !== index));
  };

  const fetchFornecedoresForSelect = async (table) => {
    try {
      let resp = { data: [] };
      if (table === 'pf') {
        resp = await oikosApi.get(`/?${qs.stringify({
          action: 'get',
          table: 'pf',
          fields: {
            pf_id: 'value',
            pf_nomeExibicao: 'label',
          },
          data: { pf_fornecedor: 1, pf_ativo: 1 },
          order: 'label',
        })}`);
      } else if (table === 'pj') {
        resp = await oikosApi.get(`/?${qs.stringify({
          action: 'get',
          table: 'pj',
          fields: {
            pj_id: 'value',
            pj_nomefanta: 'label',
          },
          data: { pj_fornecedor: 1, pj_ativo: 1 },
          order: 'label',
        })}`);
      }
      setSelFornecedores(resp.data || []);
    } catch (error) {
      console.error('Error fetching fornecedores:', error);
      setSelFornecedores([]);
    }
  };

  const resolvePedidoCompraData = (override) => ({
    ...pedidoCompraData,
    ...(override || {}),
  });

  const handleCopyOpenLink = async (override) => {
    const dataPedido = resolvePedidoCompraData(override);
    const pedidoCompraId = Number(dataPedido.pedidoCompra_id) || 0;
    if (!pedidoCompraId) {
      notifyError('Salve a cotação antes de copiar o link.');
      return;
    }

    const data = {
      vaga: isPedidoCompraVaga(dataPedido),
      pedidoCompra_id: pedidoCompraId,
      title: dataPedido.pedidoCompra_desc,
      ...(arquivosLinks.length > 0 && { files: arquivosLinks }),
    };
    const link = montaURLFormLinkAberto(data);
    await navigator.clipboard.writeText(link);
    notifySuccess('Link para compartilhamento aberto copiado!');
  };

  const handleSendEmails = async (override) => {
    if (inputsEmailsEspecificos.length === 0 && inputsEmailsFornecedores.length === 0) {
      return false;
    }

    const dataPedido = resolvePedidoCompraData(override);
    const pedidoCompraId = Number(dataPedido.pedidoCompra_id) || 0;
    if (!pedidoCompraId) {
      notifyError('Não foi possível identificar a cotação para envio dos e-mails.');
      return false;
    }

    try {
      setIsSending(true);

      const contextoPedidoHtml = buildPedidoCompraEmailContextHtml({
        pedidoCompraData: dataPedido,
      });
      const corpoBase = contextoPedidoHtml;
      const mergeCorpo = (extra) => {
        const extraText = String(extra || '').trim();
        if (!corpoBase && !extraText) return undefined;
        if (!corpoBase) return extraText;
        if (!extraText) return corpoBase;
        return `${corpoBase}<br/><br/><strong>Mensagem adicional:</strong><br/>${extraText}`;
      };

      for (let index = 0; index < inputsEmailsEspecificos.length; index += 1) {
        const element = inputsEmailsEspecificos[index];
        const link = montaURLFormLinkEmailEspecifico({
          vaga: Boolean(dataPedido.pedidoCompra_id && isPedidoCompraVaga(dataPedido)),
          pedidoCompra_id: pedidoCompraId,
          title: dataPedido.pedidoCompra_desc,
          email: element.inputEmailEspecifico_email,
          tipo: element.inputEmailEspecifico_tipo,
          ...(arquivosLinks.length > 0 && { files: arquivosLinks }),
        });
        await sendFormEmail({
          emailDest: element.inputEmailEspecifico_email,
          formLink: link,
          mensagemIntro: EMAIL_TEXT_CONVITE_PROPOSTA.mensagemIntro,
          formLinkLabel: EMAIL_TEXT_CONVITE_PROPOSTA.formLinkLabel,
          corpoTitulo: EMAIL_TEXT_CONVITE_PROPOSTA.corpoTitulo,
          corpo: mergeCorpo(corpoEmailEspecifico),
          emailEnvioTabRef: 'pedidoCompra',
          emailEnvioTabId: pedidoCompraId,
        }, EMAIL_TEXT_CONVITE_PROPOSTA.assunto);
      }

      for (let index = 0; index < inputsEmailsFornecedores.length; index += 1) {
        const element = inputsEmailsFornecedores[index];
        const link = montaURLFormLinkEmailFornecedor({
          vaga: Boolean(dataPedido.pedidoCompra_id && isPedidoCompraVaga(dataPedido)),
          pedidoCompra_id: pedidoCompraId,
          title: dataPedido.pedidoCompra_desc,
          identificador: element.pf_cpf ? element.pf_cpf : element.pj_cnpj,
          email: element.pf_emailProf ? element.pf_emailProf : element.pj_emailPrin,
          nome: element.pf_nomeExibicao ? element.pf_nomeExibicao : element.pj_nomefanta,
          tipo: element.pf_id ? 'CPF' : 'CNPJ',
          fornecedor_id: element.fornecedor_id,
          ...(arquivosLinks.length > 0 && { files: arquivosLinks }),
        });
        await sendFormEmail({
          emailDest: element.pf_emailProf ? element.pf_emailProf : element.pj_emailPrin,
          formLink: link,
          nomeDest: element.pf_nomeExibicao ? element.pf_nomeExibicao : element.pj_nomefanta,
          mensagemIntro: EMAIL_TEXT_CONVITE_PROPOSTA.mensagemIntro,
          formLinkLabel: EMAIL_TEXT_CONVITE_PROPOSTA.formLinkLabel,
          corpoTitulo: EMAIL_TEXT_CONVITE_PROPOSTA.corpoTitulo,
          corpo: mergeCorpo(corpoEmailFornecedor),
          emailEnvioTabRef: 'pedidoCompra',
          emailEnvioTabId: pedidoCompraId,
        }, EMAIL_TEXT_CONVITE_PROPOSTA.assunto);
      }

      notifySuccess('Emails enviados com sucesso!');
      return true;
    } catch (error) {
      console.error('Erro ao enviar e-mails de cotação:', error);
      notifyError('Erro ao enviar e-mails de cotação.');
      return false;
    } finally {
      setIsSending(false);
    }
  };

  useImperativeHandle(ref, () => ({
    sendEmails: handleSendEmails,
    hasEmails: () => inputsEmailsEspecificos.length > 0 || inputsEmailsFornecedores.length > 0,
    reset: resetForm,
    copyOpenLink: handleCopyOpenLink,
  }));

  const conviteEspecificoContent = (
    <FormGroup>
      <Label>
        Enviar email para:
      </Label>
      <Input
        type="email"
        placeholder="Escreva o email do fornecedor"
        disabled={disabled || isSending}
        value={inputEmailEspecifico.inputEmailEspecifico_email || ''}
        onChange={(e) => {
          setInputEmailEspecifico({
            ...inputEmailEspecifico,
            inputEmailEspecifico_email: e.target.value,
          });
        }}
      />
      <Button
        className="mt-2 btn-adicionar-proposta-pulse"
        disabled={disabled || isSending}
        onClick={handleAddEmailEspecifico}
      >
        Adicionar email
      </Button>
      {inputsEmailsEspecificos.map((input, index) => (
        <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
          <div className="d-flex justify-content-between align-items-center w-100">
            <span>{input.inputEmailEspecifico_email}</span>
          </div>
          {!disabled && (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleRemoveEmailEspecifico(index)}
            >
              Remove
            </button>
          )}
        </li>
      ))}
      <br />
      <Label>
        Corpo do email:
      </Label>
      <TextareaAutosize
        placeholder="Descreva o objeto do contrato"
        margin="dense"
        id="input_obs_especifico"
        name="input_obs_especifico"
        variant="standard"
        className="mb-3 p-1"
        style={{ width: '100%', height: '100px' }}
        disabled={disabled || isSending}
        value={corpoEmailEspecifico}
        onChange={(e) => setCorpoEmailEspecifico(e.target.value)}
      />
    </FormGroup>
  );

  const linkCompartilhamentoButton = !hideLinkCompartilhamentoAberto ? (
    <Button
      className="mt-2"
      disabled={disabled || isSending}
      onClick={() => handleCopyOpenLink()}
    >
      Link para compartilhamento aberto
    </Button>
  ) : null;

  // Quando só existe "Convite específico", mostra um fluxo guiado e opcional,
  // deixando claro que o usuário pode recolher propostas por e-mail se quiser.
  if (hideBancoFornecedores) {
    const temEmailsAdicionados = inputsEmailsEspecificos.length > 0;
    return (
      <Col sm={12} className="px-0">
        {linkCompartilhamentoButton}
        <Card className="border mt-2">
          <CardBody>
            <div className="d-flex align-items-start justify-content-between flex-wrap">
              <div className="mb-2 mr-3">
                <h5 className="mb-1">Já quer recolher propostas por e-mail?</h5>
                <p className="text-muted mb-0">
                  Este passo é <strong>opcional</strong>. Se quiser, convide fornecedores
                  específicos: cada e-mail adicionado receberá um link para enviar a proposta.
                  Você também pode abrir a cotação agora e convidar fornecedores depois.
                </p>
              </div>
            </div>

            <hr className="my-3" />

            <FormGroup className="mb-2">
              <Label className="font-weight-bold mb-1" htmlFor="input_email_especifico">
                E-mail do fornecedor
              </Label>
              <InputGroup>
                <Input
                  id="input_email_especifico"
                  type="email"
                  placeholder="Ex.: contato@fornecedor.com"
                  disabled={disabled || isSending}
                  value={inputEmailEspecifico.inputEmailEspecifico_email || ''}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddEmailEspecifico();
                    }
                  }}
                  onChange={(e) => {
                    setInputEmailEspecifico({
                      ...inputEmailEspecifico,
                      inputEmailEspecifico_email: e.target.value,
                    });
                  }}
                />
                <InputGroupAddon addonType="append">
                  <Button
                    color="primary"
                    className="btn-adicionar-proposta-pulse"
                    disabled={disabled || isSending}
                    onClick={handleAddEmailEspecifico}
                  >
                    <i className="mdi mdi-plus align-middle mr-1" />
                    Adicionar e-mail
                  </Button>
                </InputGroupAddon>
              </InputGroup>
              <small className="text-muted">
                Digite o e-mail e clique em <strong>Adicionar e-mail</strong> (ou pressione Enter)
                para incluí-lo na lista de convites.
              </small>
            </FormGroup>

            {temEmailsAdicionados ? (
              <div className="mb-3">
                <Label className="font-weight-bold mb-1">
                  Convites a enviar ({inputsEmailsEspecificos.length})
                </Label>
                <ul className="list-group">
                  {inputsEmailsEspecificos.map((input, index) => (
                    <li
                      key={index}
                      className="list-group-item d-flex justify-content-between align-items-center"
                    >
                      <span className="d-flex align-items-center">
                        <i className="mdi mdi-email-outline text-primary mr-2" />
                        {input.inputEmailEspecifico_email}
                      </span>
                      {!disabled && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleRemoveEmailEspecifico(index)}
                        >
                          Remover
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="text-muted small mb-3 d-flex align-items-center">
                <i className="mdi mdi-information-outline mr-1" />
                Nenhum fornecedor convidado ainda. Adicione um e-mail acima se quiser recolher propostas.
              </div>
            )}

            {temEmailsAdicionados && (
              <FormGroup className="mb-0">
                <Label className="font-weight-bold mb-1" htmlFor="input_obs_especifico">
                  Mensagem do convite (opcional)
                </Label>
                <TextareaAutosize
                  placeholder="Escreva uma mensagem que acompanhará o convite enviado aos fornecedores."
                  margin="dense"
                  id="input_obs_especifico"
                  name="input_obs_especifico"
                  variant="standard"
                  className="p-2 form-control"
                  style={{ width: '100%', minHeight: '90px' }}
                  disabled={disabled || isSending}
                  value={corpoEmailEspecifico}
                  onChange={(e) => setCorpoEmailEspecifico(e.target.value)}
                />
                <small className="text-muted">
                  Esta mensagem será incluída no corpo do e-mail junto com o link da proposta.
                </small>
              </FormGroup>
            )}
          </CardBody>
        </Card>
      </Col>
    );
  }

  return (
    <Col sm={12} className="px-0">
      <Tabs>
        <div className="d-flex align-items-center justify-content-between flex-wrap">
          <TabList>
            <Tab>Convite específico</Tab>
            <Tab>Banco de fornecedores</Tab>
          </TabList>
          {linkCompartilhamentoButton}
        </div>
        <TabPanel>
          {conviteEspecificoContent}
        </TabPanel>

        <TabPanel>
          <FormGroup>
            <Label>
              Tipo de fornecedor:
            </Label>
            <Select
              classNamePrefix="select2-selection"
              placeholder="PF ou PJ"
              isDisabled={disabled || isSending}
              value={[{ value: 'pf', label: 'Pessoa Física' }, { value: 'pj', label: 'Pessoa Jurídica' }]
                .filter((x) => x.value === inputEmailFornecedor.table)}
              options={[
                { value: 'pf', label: 'Pessoa Física' },
                { value: 'pj', label: 'Pessoa Jurídica' },
              ]}
              onChange={(e) => {
                setInputEmailFornecedor({ table: e.value });
                fetchFornecedoresForSelect(e.value);
              }}
            />

            <Label>
              Fornecedor:
            </Label>
            <Select
              classNamePrefix="select2-selection"
              placeholder="Selecione um item"
              isDisabled={disabled || isSending}
              value={inputEmailFornecedor.fornecedor_id
                ? selFornecedores.filter((x) => x.value === inputEmailFornecedor.fornecedor_id)
                : null}
              options={selFornecedores}
              onChange={(e) => {
                setInputEmailFornecedor({ ...e, ...inputEmailFornecedor, fornecedor_id: e.value });
              }}
            />

            <Button
              className="mt-2 btn-adicionar-proposta-pulse"
              disabled={disabled || isSending}
              onClick={handleAddEmailFornecedor}
            >
              Adicionar email
            </Button>
            {inputsEmailsFornecedores.map((input, index) => (
              <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center w-100">
                  <span className="mr-5">
                    {input.pf_id ? input.pf_nomeExibicao : input.pj_nomefanta}
                  </span>
                  <span>
                    {input.pf_id ? input.pf_emailProf : input.pj_emailPrin}
                  </span>
                </div>
                {!disabled && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleRemoveEmailFornecedor(index)}
                  >
                    Remove
                  </button>
                )}
              </li>
            ))}
            <br />
            <Label>
              Corpo do email:
            </Label>
            <TextareaAutosize
              placeholder="Descreva o objeto do contrato"
              margin="dense"
              id="input_obs_fornecedor"
              name="input_obs_fornecedor"
              variant="standard"
              className="mb-3 p-1"
              style={{ width: '100%', height: '100px' }}
              disabled={disabled || isSending}
              value={corpoEmailFornecedor}
              onChange={(e) => setCorpoEmailFornecedor(e.target.value)}
            />
          </FormGroup>
        </TabPanel>
      </Tabs>
    </Col>
  );
});

DisparoEmailsCotacaoForm.displayName = 'DisparoEmailsCotacaoForm';

export default DisparoEmailsCotacaoForm;
